import similarity from 'similarity'
import { RegexHelper } from './regex'
import { Utility } from './utility'

type Edit = {
  original: string
  update: string
}

type ApplyEditOptions = {
  onlyIfPerfectMatch?: boolean
}

type ApplyManyEditsOptions = {
  isStrict?: boolean
  isLax?: boolean
}

type Handler = (
  content: string,
  edit: Edit,
  options: ApplyManyEditsOptions,
) => string

export type DiffEdit = Edit

const EMPTY_CHARACTERS = ['null']

export namespace DiffHelper {
  export function extractEdits(content: string): Edit[] {
    const REGEX_EDIT_PATTERN = /<<<+ SEARCH\n(.*?)===+\n(.*?)>>>+ REPLACE/gs
    const edits: Edit[] = []

    checkEditsFormat(content)

    const matches = RegexHelper.findRegexCaptures(content, REGEX_EDIT_PATTERN)
    for (const match of matches) {
      let original = match[0]
      const update = match[1]

      if (EMPTY_CHARACTERS.includes(original.trim())) {
        original = '\n'
      }

      edits.push({ original, update })
    }

    return edits
  }

  export function applyEdits(
    contentFile: string,
    edits: Edit[] = [],
    options?: ApplyEditOptions,
  ): string {
    if (Utility.isEmpty(edits)) {
      return contentFile
    }

    const failed = []
    const passed = []

    let contentFileUpdated = contentFile

    for (const edit of edits) {
      const { original, update } = edit

      const contentNew = applyEdit(
        contentFileUpdated,
        original,
        update,
        options,
      )

      if (contentNew) {
        passed.push(edit)
        contentFileUpdated = contentNew
      } else {
        failed.push(edit)
      }
    }

    const hasAnyEditFailed = failed.length > 0
    const haveAllEditsFailed = failed.length === edits.length
    if (haveAllEditsFailed) {
      throw new Error(`Failed to apply edits: ${Utility.stringify(failed)}`)
    }

    if (hasAnyEditFailed) {
      console.warn(`Failed to apply some edits: ${Utility.stringify(failed)}`)
    }

    return contentFileUpdated
  }

  export function applyManyEdits(
    content: string = '',
    edits: Edit[] = [],
    options: ApplyManyEditsOptions = {},
  ) {
    const { isStrict = false, isLax = true } = options

    if (edits.length === 0) {
      return { content, editsSuccess: [], editsFailed: [] }
    }

    const editsSuccess: Edit[] = []
    const editsFailed: Edit[] = []

    let contentUpdated = content

    for (const edit of edits) {
      contentUpdated = applyOneEdit(contentUpdated, edit, { isLax })

      const isSuccess = contentUpdated !== content

      if (isSuccess) {
        editsSuccess.push(edit)
      } else {
        editsFailed.push(edit)
      }
    }

    const isCompletelyFailed =
      (isStrict && editsFailed.length > 0) || editsSuccess.length === 0

    const errorMessage = `Could not apply ${editsFailed.length}/${edits.length} edits: ${Utility.stringify(editsFailed)}`

    if (editsFailed.length > 0) {
      console.warn(errorMessage)
    }

    if (isCompletelyFailed) {
      throw new Error(errorMessage)
    }

    return { content: contentUpdated, editsSuccess, editsFailed }
  }

  export function getDistance(
    original: string,
    updated: string,
    precision?: number,
  ): number {
    const raw = similarity(original, updated)

    return precision ? parseFloat(raw.toFixed(precision)) : raw
  }

  /* -------------------------------------------------------------------------- */
  /*                                   PRIVATE                                  */
  /* -------------------------------------------------------------------------- */

  function applyOneEdit(
    content: string,
    edit: Edit,
    options: ApplyManyEditsOptions,
  ) {
    const handlers: Handler[] = [applySimplest]

    for (const handler of handlers) {
      const contentUpdated = handler(content, edit, options)

      if (contentUpdated !== content) {
        return contentUpdated
      }
    }
    return content
  }

  function applySimplest(
    content: string,
    edit: Edit,
    options: ApplyManyEditsOptions,
  ) {
    const isOriginalFound = content.includes(edit.original)
    const isOriginalAlone = content.split(edit.original)

    if (isOriginalFound && isOriginalAlone) {
      const contentUpdated = content.replace(edit.original, edit.update)

      return contentUpdated
    }

    if (options.isLax) {
      return applySimplest(
        content,
        { original: edit.original.trim(), update: edit.update },
        { isLax: false },
      )
    }

    return content
  }

  function checkEditsFormat(content: string) {
    const searchPattern = /<<<+ SEARCH\n/g
    const midPattern = /===+\n/g
    const replacePattern = />>>+ REPLACE/g

    const searchMatches = RegexHelper.findRegexes(content, searchPattern)
    const midMatches = RegexHelper.findRegexes(content, midPattern)
    const replaceMatches = RegexHelper.findRegexes(content, replacePattern)

    if (
      searchMatches.length !== midMatches.length ||
      midMatches.length !== replaceMatches.length
    ) {
      throw new Error('Edits are not formatted correctly')
    }
  }

  function applyEdit(
    content: string,
    original: string,
    update: string,
    options?: ApplyEditOptions,
  ): string {
    const originalUnquoted = ensureUnquoted(original).trimEnd()
    const updateUnquoted = ensureUnquoted(update)
    const contentLines = content.split('\n')
    const originalLines = original.split('\n')
    const updateLines = update.split('\n')

    if (Utility.isNull(originalUnquoted)) {
      return content + updateUnquoted
    }

    const contentUpdated = replacePerfectOrByOutdenting(
      contentLines,
      originalLines,
      updateLines,
    )

    if (contentUpdated) {
      return contentUpdated
    }

    if (options?.onlyIfPerfectMatch) {
      return null
    }

    const ContentNew = replaceMostSimilar(
      content,
      contentLines,
      originalLines,
      updateLines,
    )

    return ContentNew
  }

  function replaceMostSimilar(
    content: string,
    contentLines: string[],
    originalLines: string[],
    updateLines: string[],
  ): string {
    let contentUpdated = null

    //? AI sometimes add an extra line at the beginning that is not part of the original
    const isOriginalFirstLineEmpty =
      originalLines.length > 1 && !originalLines[0].trim()

    if (isOriginalFirstLineEmpty) {
      const originalLinesCleaned = originalLines.slice(1)

      contentUpdated = replacePerfectOrByOutdenting(
        contentLines,
        originalLinesCleaned,
        updateLines,
      )

      if (contentUpdated) {
        console.log('Replaced by removing first line')
        return contentUpdated
      }
    }

    contentUpdated = replaceClosestSimilarity(
      contentLines,
      originalLines.join('\n'),
      originalLines,
      updateLines,
    )

    if (contentUpdated) {
      console.log('Replaced by closest similarity')
      return contentUpdated
    }
  }

  function replacePerfectOrByOutdenting(
    contentLines: string[],
    originalLines: string[],
    updateLines: string[],
  ): string {
    let content = replacePerfect(contentLines, originalLines, updateLines)

    if (content) {
      console.log('Replaced perfectly')
      return content
    }

    const originalLinesTrimmed = removeTrailingEmptyLines(originalLines)
    const updateLinesTrimmed = removeTrailingEmptyLines(updateLines)
    content = replacePerfect(
      contentLines,
      originalLinesTrimmed,
      updateLinesTrimmed,
    )

    if (content) {
      console.log('Replaced by removing trailing empty lines')
      return content
    }

    content = replaceByOutdenting(contentLines, originalLines, updateLines)

    if (content) {
      console.log('Replaced by outdenting')
      return content
    }

    return null
  }

  function replacePerfect(
    contentLines: string[],
    originalLines: string[],
    updateLines: string[],
  ): string {
    const linesCountDifference = contentLines.length - originalLines.length

    for (let index = 0; index <= linesCountDifference; index++) {
      const contentChunck = contentLines
        .slice(index, index + originalLines.length)
        .join('\n')
      const originalChunck = originalLines.join('\n')

      if (contentChunck === originalChunck) {
        const contentStart = contentLines.slice(0, index)
        const contentEnd = contentLines.slice(index + originalLines.length)
        const contentNew = [...contentStart, ...updateLines, ...contentEnd]

        return contentNew.join('\n')
      }
    }
  }

  function replaceByOutdenting(
    contentLines: string[],
    originalLines: string[],
    updateLines: string[],
  ): string | null {
    const originalLinesIndentationCounts = originalLines
      .filter(line => line.trim())
      .map(line => countIndentation(line))

    const updateLinesIndentationCounts = updateLines
      .filter(line => line.trim())
      .map(line => countIndentation(line))

    const indentationCounts = originalLinesIndentationCounts.concat(
      updateLinesIndentationCounts,
    )

    const hasIndentationAtStart =
      indentationCounts.length > 0 && Math.min(...indentationCounts) > 0

    let originalLinesOutdentedStart = originalLines
    let updateLinesOutdentedStart = updateLines

    if (hasIndentationAtStart) {
      const indentationToRemove = Math.min(...indentationCounts)

      originalLinesOutdentedStart = originalLines.map(line =>
        line.trim() ? line.slice(indentationToRemove) : line,
      )
      updateLinesOutdentedStart = updateLines.map(line =>
        line.trim() ? line.slice(indentationToRemove) : line,
      )
    }

    const linesCountDifference =
      contentLines.length - originalLinesOutdentedStart.length

    for (let index = 0; index <= linesCountDifference; index++) {
      const contentLinesSliced = contentLines.slice(
        index,
        index + originalLinesOutdentedStart.length,
      )

      const areLinesMatching = contentLinesSliced
        .map((line, index) => line.trim() === originalLines[index].trim())
        .every(Boolean)

      if (!areLinesMatching) {
        continue
      }

      const indentationCount =
        contentLinesSliced[0].length - originalLinesOutdentedStart[0].length

      const indentation = ' '.repeat(indentationCount)

      const updateLinesUpdated = updateLinesOutdentedStart.map(rline =>
        rline.trim() ? indentation + rline : rline,
      )

      const contentStart = contentLines.slice(0, index)
      const contentEnd = contentLines.slice(
        index + originalLinesOutdentedStart.length,
      )

      const contentUpdated = [
        ...contentStart,
        ...updateLinesUpdated,
        ...contentEnd,
      ]

      return contentUpdated.join('\n')
    }

    return null
  }

  function replaceClosestSimilarity(
    contentLines: string[],
    original: string,
    originalLines: string[],
    updateLines: string[],
  ): string {
    const similarityThresh = 0.8

    let maxSimilarity = 0
    let mostSimilarChunkStart = -1
    let mostSimilarChunkEnd = -1

    const scale = 0.1
    const minLen = Math.floor(originalLines.length * (1 - scale))
    const maxLen = Math.ceil(originalLines.length * (1 + scale))
    for (let length = minLen; length < maxLen; length++) {
      for (let index = 0; index < contentLines.length - length + 1; index++) {
        const chunk = contentLines.slice(index, index + length).join('\n')

        try {
          const similarityScore = similarity(chunk, original)

          if (similarityScore > maxSimilarity && similarityScore) {
            maxSimilarity = similarityScore
            mostSimilarChunkStart = index
            mostSimilarChunkEnd = index + length
          }
        } catch (error) {
          console.error(`Error comparing content: ${error}`)
          continue
        }
      }
    }

    if (maxSimilarity < similarityThresh) {
      return
    }

    const contentStart = contentLines.slice(0, mostSimilarChunkStart)

    const isLastLineEmptyString = Utility.isNull(
      contentLines[mostSimilarChunkEnd],
    )

    const contentEnd = !isLastLineEmptyString
      ? contentLines.slice(mostSimilarChunkEnd)
      : contentLines.slice(mostSimilarChunkEnd - 1)

    const updateLinesClean = [...updateLines]

    const contentUpdated =
      [...contentStart, ...updateLinesClean].join('\n') + contentEnd.join('\n')

    return contentUpdated
  }

  function ensureUnquoted(content: string): string {
    if (!content) {
      return content
    }

    let lines = content.split('\n')

    const isQuoted =
      (lines[0].startsWith('"""') || lines[0].startsWith('```')) &&
      (lines[lines.length - 1].startsWith('"""') ||
        lines[lines.length - 1].startsWith('```'))

    if (isQuoted) {
      lines = lines.slice(1, -1)
    }

    const contentUpdated = lines.join('\n')

    return contentUpdated
  }

  function countIndentation(line: string): number {
    return line.length - line.trimStart().length
  }

  function removeTrailingEmptyLines(lines: string[]): string[] {
    if (Utility.isEmpty(lines)) {
      return lines
    }

    const trimmedLines = [...lines]

    while (
      trimmedLines.length > 0 &&
      trimmedLines[trimmedLines.length - 1].trim() === ''
    ) {
      trimmedLines.pop()
    }

    return trimmedLines
  }
}
